import { Autocomplete, Box, Button, TextField } from "@mui/material";
import { IAddress } from "../../../../models/address";
import { IDock } from "../../../../models/dock";
import { IShipDispatcher } from "../../../../models/shipDispatcher";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { useIntl } from "react-intl";
import { DateFormat, getDateMoment, getDateNumber } from "../../../../helpers/dateHelper";
import { getKeyByValue } from "../../../../helpers/mapHelper";
import Messages from "../../../../localization/Messages";
import { StatisticsFilters as StatisticsFiltersType } from "../../shared/filters";
import { DISP_STATISTICS_FILTERS_INIT } from "../../../../store/guiConfsReducer";

export type StatisticsFiltersProps = {
    managingCompanies: Map<any, any>;
    owningCompanies: IAddress[];
    ships: IShipDispatcher[];
    docks: IDock[];
    filters: StatisticsFiltersType;
    setFilters: (filters: Partial<StatisticsFiltersType>) => void;
};

export const StatisticsFilters = ({
    managingCompanies,
    owningCompanies,
    ships,
    docks,
    filters,
    setFilters,
}: StatisticsFiltersProps) => {
    const intl = useIntl();

    const clearFilters = () => {
        setFilters(DISP_STATISTICS_FILTERS_INIT);
    };

    return (
        <Box
            className="statistics-filters-container"
            display="flex"
            justifyContent={"flex-start"}
            alignItems={"center"}
            flexDirection={"row"}
            flexWrap={"wrap"}
            gap={2}
        >
            <DesktopDatePicker
                label={intl.formatMessage(Messages.from)}
                inputFormat={DateFormat.CLIENT_DATE}
                value={filters.from}
                onChange={value =>
                    (!isNaN(getDateNumber(value)) || value === null) &&
                    setFilters({ from: getDateMoment(value).valueOf() })
                }
                renderInput={params => (
                    <TextField {...params} sx={{ minWidth: 220 }} variant="filled" />
                )}
            />

            <DesktopDatePicker
                label={intl.formatMessage(Messages.to)}
                inputFormat={DateFormat.CLIENT_DATE}
                value={filters.to}
                onChange={value =>
                    (!isNaN(getDateNumber(value)) || value === null) &&
                    setFilters({ to: getDateMoment(value).valueOf() })
                }
                renderInput={params => (
                    <TextField {...params} sx={{ minWidth: 220 }} variant="filled" />
                )}
            />

            <Autocomplete
                sx={{ minWidth: 250 }}
                id="statistics-owning-company"
                options={owningCompanies}
                getOptionLabel={option => option.company ?? ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={props => (
                    <TextField
                        {...props}
                        fullWidth
                        label={intl.formatMessage(Messages.owningCompany)}
                        variant="filled"
                    />
                )}
                renderOption={(props, option) => <li {...props}>{option.company}</li>}
                onChange={(_, value) => {
                    setFilters({ owningCompanyId: value?.id });
                }}
                value={
                    owningCompanies.find(company => company.id === filters.owningCompanyId) ?? null
                }
            />

            <Autocomplete
                sx={{ minWidth: 250 }}
                id="statistics-managing-company"
                options={[...managingCompanies.values()]}
                getOptionLabel={option => option ?? ""}
                isOptionEqualToValue={(option, value) => option === value}
                renderInput={props => (
                    <TextField
                        {...props}
                        label={intl.formatMessage(Messages.managingCompany)}
                        variant="filled"
                    />
                )}
                renderOption={(props, option) => <li {...props}>{option}</li>}
                onChange={(_, value) => {
                    const id = getKeyByValue(managingCompanies, value);

                    setFilters({ managingCompanyId: id });
                }}
                value={managingCompanies.get(filters.managingCompanyId) ?? null}
            />

            <Autocomplete
                sx={{ minWidth: 250 }}
                id="ship-autocomplete"
                options={ships}
                getOptionLabel={o => o.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={params => (
                    <TextField
                        {...params}
                        variant="filled"
                        label={intl.formatMessage(Messages.ship)}
                    />
                )}
                renderOption={(props, option) => {
                    return (
                        <li {...props} key={option.id}>
                            {option.name}
                        </li>
                    );
                }}
                onChange={(_, value: IShipDispatcher | null) => setFilters({ shipId: value?.id })}
                value={ships.find(s => s.id === filters.shipId) ?? null}
            />

            <Autocomplete
                options={docks}
                sx={{ minWidth: 250 }}
                onChange={(_, value: IDock | null) => setFilters({ dockId: value?.id })}
                value={docks.find(s => s.id === filters.dockId) ?? null}
                getOptionLabel={o => o.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={params => (
                    <TextField
                        {...params}
                        variant="filled"
                        label={intl.formatMessage(Messages.dock)}
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        {option.name}
                    </li>
                )}
            />

            <Box display="flex" mt={2} flex="1">
                {(filters.to ||
                    filters.from ||
                    filters.managingCompanyId ||
                    filters.owningCompanyId ||
                    filters.dockId ||
                    filters.shipId) && (
                    <Button variant="outlined" onClick={clearFilters}>
                        {intl.formatMessage(Messages.clear)}
                    </Button>
                )}
            </Box>
        </Box>
    );
};
